import { Fragment, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { debounce } from "lodash-es";
import {
  Groups as GroupsIcon,
  Language as LanguageIcon,
} from "@mui/icons-material";
import { useViewer } from "~/providers/viewer";
import {
  useGetWorkspaceQuery,
  useGetWorkspacesListQuery,
  useLoadOrganizationStatsQuery,
} from "~/operations";
import { Config } from "~/constants/configuration_provider";
import { ScopeButton, ScopeButtonProps, ScopeOption } from ".";
import { useOrgAndSpaceData } from "~/hooks";
import { useSearchParams } from "react-router-dom";
import { useRegions } from "~/providers/RegionsProvider";
import { SpacesIcon, WorkspacesIcon } from "~/icons";
export function AppNavBreadcrumbs() {
  const isPrivateInstance = Config.VITE_PRIVATE_INSTANCE === "true";
  const [searchParams] = useSearchParams();
  const orgId = searchParams.get("organizationId");
  const spaceId = searchParams.get("spaceId");
  const workspaceMrn = searchParams.get("workspaceId");
  const { orgData: org, spaceData: space } = useOrgAndSpaceData({
    orgId,
    spaceId,
  });

  const { viewer } = useViewer();
  const { regions, selectedRegion, updateSelectedRegion } = useRegions();

  const [orgQuery, setOrgQuery] = useState("");
  const [spaceQuery, setSpaceQuery] = useState("");
  const [workspaceQuery, setWorkspaceQuery] = useState("");
  const [selectedOrgOption, setSelectedOrgOption] = useState<ScopeOption>();
  const [selectedSpaceOption, setSelectedSpaceOption] = useState<ScopeOption>();
  const [selectedWorkspaceOption, setSelectedWorkspaceOption] =
    useState<ScopeOption>();

  const { data: workspaceData } = useGetWorkspaceQuery({
    variables: { mrn: workspaceMrn || "skip" },
    skip: !workspaceMrn,
  });
  const workspace =
    workspaceData?.workspace.__typename === "Workspace"
      ? workspaceData.workspace
      : undefined;

  const { data: spaceWorkspacesData } = useGetWorkspacesListQuery({
    variables: { input: { scopeMrn: space?.mrn || "skip" } },
    skip: !space?.mrn,
  });
  const spaceWorkspaces =
    spaceWorkspacesData?.workspaces.edges?.map(({ node }) => node) || [];

  const { data: orgStatsData, refetch: refetchOrgStats } =
    useLoadOrganizationStatsQuery({
      variables: { mrn: org?.mrn || "" },
      skip: !org?.mrn,
    });

  const fetchOrgStats = useMemo(
    () => debounce(refetchOrgStats, 300),
    [refetchOrgStats],
  );

  // Build region options
  const regionOptions =
    regions?.map(({ name }) => ({ id: name, name, href: "/dashboard" })) || [];
  const selectedRegionOption = regionOptions.find(
    ({ name }) => name === selectedRegion.name,
  );

  // Build org options
  const orgs =
    viewer?.organizations?.map(({ id, name }) => ({
      id,
      name,
      href: `/organization/overview?organizationId=${id}`,
    })) || [];

  const orgOptions = orgs.filter((o) =>
    o.name.toLowerCase().includes(orgQuery.toLowerCase()),
  );

  useEffect(() => {
    const selectedOrg = orgs.find((o) => o.id === org?.id);
    if (selectedOrg) {
      setSelectedOrgOption(selectedOrg);
    }
  }, [org]);

  // Build space options
  const orgSpaces =
    orgStatsData?.organization?.spacesList.edges.flatMap((e) => e.node ?? []) ||
    [];

  const spaceOptions =
    orgSpaces.map(({ id, name }) => ({
      id,
      name,
      href: `/space/overview?spaceId=${id}`,
    })) || [];

  useEffect(() => {
    if (space) {
      setSelectedSpaceOption({
        id: space.id,
        name: space.name,
        href: `/space/overview?spaceId=${space.id}`,
      });
    }
  }, [space]);

  // Build workspace options
  const workspaceOptions = spaceWorkspaces
    .filter((workspace) =>
      workspace.name.toLowerCase().includes(workspaceQuery.toLowerCase()),
    )
    .map((workspace) => ({
      id: workspace.mrn,
      name: workspace.name,
      href: `/space/overview?spaceId=${space?.id}&workspaceId=${workspace.mrn}`,
    }));

  useEffect(() => {
    if (space && workspace) {
      setSelectedWorkspaceOption({
        id: workspace.mrn,
        name: workspace.name,
        href: `/space/overview?spaceId=${space.id}&workspaceId=${workspace.mrn}`,
      });
    }
  }, [space, workspace]);

  useEffect(() => {
    if (!workspaceMrn) {
      setSelectedWorkspaceOption(undefined);
      if (!spaceId) {
        setSelectedSpaceOption(undefined);
        if (!orgId) {
          setSelectedOrgOption(undefined);
        }
      }
    }
  }, [orgId, spaceId, workspaceMrn]);

  const onOrgQuery: ScopeButtonProps["onQuery"] = (query) => {
    setOrgQuery(query);
  };

  const onSpaceQuery: ScopeButtonProps["onQuery"] = (query) => {
    setSpaceQuery(query);
    fetchOrgStats({ query });
  };

  const onWorkspaceQuery: ScopeButtonProps["onQuery"] = (query) => {
    setWorkspaceQuery(query);
  };

  const onRegionSelect: ScopeButtonProps["onSelect"] = (option) => {
    const region = regions?.find((r) => r.name === option.name);
    if (region) {
      updateSelectedRegion(region);
    }
  };

  const onOrgSelect: ScopeButtonProps["onSelect"] = (option) => {
    setSelectedOrgOption(option);
    setSelectedSpaceOption(undefined);
    setOrgQuery("");
    setSpaceQuery("");
  };

  const onSpaceSelect: ScopeButtonProps["onSelect"] = (option) => {
    setSelectedSpaceOption(option);
    setSpaceQuery("");
    fetchOrgStats({ query: "" });
  };

  const onWorkspaceSelect: ScopeButtonProps["onSelect"] = (option) => {
    setSelectedWorkspaceOption(option);
    setWorkspaceQuery("");
  };

  const separator = (
    <Typography aria-hidden color="text.secondary" fontSize={13} children="›" />
  );

  const allSpacesHref = selectedOrgOption
    ? `/organization/spaces?organizationId=${selectedOrgOption.id}`
    : undefined;

  return (
    <Box id="app-nav-breadcrumbs" sx={{ mr: "auto", pl: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {!isPrivateInstance && (
          <Fragment>
            <ScopeButton
              scope="regions"
              icon={<LanguageIcon />}
              options={regionOptions}
              selected={selectedRegionOption}
              onSelect={onRegionSelect}
            />
            {separator}
          </Fragment>
        )}
        <ScopeButton
          scope="organizations"
          icon={<GroupsIcon />}
          options={orgOptions}
          selected={selectedOrgOption}
          onSelect={onOrgSelect}
          query={orgQuery}
          onQuery={onOrgQuery}
          allHref={"/dashboard"}
        />
        {selectedOrgOption && (
          <Fragment>
            {separator}
            <ScopeButton
              scope="spaces"
              icon={<SpacesIcon />}
              options={spaceOptions}
              selected={selectedSpaceOption}
              onSelect={onSpaceSelect}
              query={spaceQuery}
              onQuery={onSpaceQuery}
              allHref={allSpacesHref}
            />
          </Fragment>
        )}
        {selectedSpaceOption && (
          <Fragment>
            {separator}
            <ScopeButton
              scope="workspaces"
              icon={<WorkspacesIcon />}
              options={workspaceOptions}
              selected={selectedWorkspaceOption}
              onSelect={onWorkspaceSelect}
              query={workspaceQuery}
              onQuery={onWorkspaceQuery}
              allHref={`/space/workspaces?spaceId=${selectedSpaceOption.id}`}
            />
          </Fragment>
        )}
      </Box>
    </Box>
  );
}
